import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';

// Define the notification type
export interface SnackbarNotification {
  message: string;
  type: 'error' | 'success' | 'warning';
}

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {
  // Keep only the subject for custom notifications
  notificationSubject = new Subject<SnackbarNotification>();
  
  // Track recent messages to prevent duplicates
  private recentMessages: {message: string, timestamp: number}[] = [];
  private messageDeduplicationWindow = 1000; // in milliseconds

  /**
   * Shows an error notification
   * @param message The error message to display
   */
  error(message: string) {
    if (this.isDuplicateMessage(message)) return;
    this.notificationSubject.next({ message, type: 'error' });
    this.trackMessage(message);
  }

  /**
   * Shows a success notification
   * @param message The success message to display
   */
  success(message: string) {
    if (this.isDuplicateMessage(message)) return;
    this.notificationSubject.next({ message, type: 'success' });
    this.trackMessage(message);
  }

  /**
   * Shows a warning notification
   * @param message The warning message to display
   */
  warning(message: string) {
    if (this.isDuplicateMessage(message)) return;
    this.notificationSubject.next({ message, type: 'warning' });
    this.trackMessage(message);
  }

  /**
   * Checks if a message was recently shown to prevent duplicates
   */
  private isDuplicateMessage(message: string): boolean {
    const now = Date.now();
    // Clean up old messages first
    this.recentMessages = this.recentMessages.filter(
      item => (now - item.timestamp) < this.messageDeduplicationWindow
    );
    
    return this.recentMessages.some(item => item.message === message);
  }

  /**
   * Tracks a message to prevent duplicates
   */
  private trackMessage(message: string): void {
    this.recentMessages.push({
      message,
      timestamp: Date.now()
    });
  }
}
